import type {
  ApiResponse,
  User,
  LoginCredentials,
  RegisterData,
  InviteCode,
  PaginatedResponse,
} from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

class ApiClient {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${API_BASE_URL}${endpoint}`;

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      credentials: 'include', // 包含 cookies
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || '请求失败');
    }

    return data;
  }

  // 认证相关
  auth = {
    login: (credentials: LoginCredentials) =>
      this.request<{ user: User }>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify(credentials),
      }),

    register: (data: RegisterData) =>
      this.request<{ user: User }>('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    verify: () => this.request<{ user: User }>('/api/auth/verify'),

    logout: () =>
      this.request('/api/auth/logout', {
        method: 'POST',
      }),
  };

  // 邀请码相关
  invite = {
    generate: (count: number, expiresIn?: number) =>
      this.request<{ codes: string[] }>('/api/invite/generate', {
        method: 'POST',
        body: JSON.stringify({ count, expiresIn }),
      }),

    list: (page: number = 1, limit: number = 20, status: string = 'all') =>
      this.request<PaginatedResponse<InviteCode>>(
        `/api/invite/list?page=${page}&limit=${limit}&status=${status}`
      ),

    validate: (code: string) =>
      this.request<{ valid: boolean }>('/api/invite/validate', {
        method: 'POST',
        body: JSON.stringify({ code }),
      }),
  };

  // 收藏相关
  favorites = {
    add: (promptId: string) =>
      this.request('/api/favorites/add', {
        method: 'POST',
        body: JSON.stringify({ promptId }),
      }),

    remove: (promptId: string) =>
      this.request('/api/favorites/remove', {
        method: 'DELETE',
        body: JSON.stringify({ promptId }),
      }),

    list: () => this.request<{ favorites: string[] }>('/api/favorites/list'),
  };

  // 用户相关
  user = {
    profile: () => this.request<{ user: User }>('/api/user/profile'),

    updatePassword: (currentPassword: string, newPassword: string) =>
      this.request('/api/user/password', {
        method: 'PUT',
        body: JSON.stringify({ currentPassword, newPassword }),
      }),

    updatePermissions: (userId: string, permissionGroups: string[]) =>
      this.request('/api/user/permissions', {
        method: 'PUT',
        body: JSON.stringify({ userId, permissionGroups }),
      }),
  };

  // Prompt 相关
  prompts = {
    list: (params?: {
      category?: string;
      permission?: string;
      search?: string;
      page?: number;
      limit?: number;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.category) searchParams.set('category', params.category);
      if (params?.permission) searchParams.set('permission', params.permission);
      if (params?.search) searchParams.set('search', params.search);
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.limit) searchParams.set('limit', params.limit.toString());

      const query = searchParams.toString();
      return this.request<any>(`/api/prompts/list${query ? `?${query}` : ''}`);
    },

    get: (id: string) =>
      this.request<any>(`/api/prompts/${id}`),

    create: (data: any) =>
      this.request<any>('/api/prompts/create', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: any) =>
      this.request<any>(`/api/prompts/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      this.request<any>(`/api/prompts/${id}`, {
        method: 'DELETE',
      }),
  };
}

export const api = new ApiClient();

